<template>
    <validation-observer 
        tag="div" 
        ref="formObserver"
        :disabled="localDisabled"
        class="p-3"  
        v-slot="slotProps"
    >
        <form @submit.prevent="slotProps.handleSubmit(onSubmit)">
            <CustomFormSchema 
                ref="rootFormSchema"
                :schema="schema"
                :initialData="initialData"
                :level="0"
                :disabled="localDisabled"
                :displayMode="displayMode"
                class="d-flex flex-column gap-3 flex-wrap" 
                @update="handleUpdate"
                @mounted="handleRootSchemaMounted"
            />  <!-- path="root" -->

            <div class="d-flex gap-3 align-items-center">
                <b-button type="submit" :disabled="slotProps.invalid" size="lg" variant="primary">Submit, Yo</b-button>
                <b-button @click="runValidation" size="lg" variant="gray-primary">Validate</b-button>
                <b-button @click="resetValidation" size="lg" variant="gray-primary">Reset Validation</b-button>
            </div>

            <div class="">
                <div v-for="attr in ['dirty', 'pristine', 'valid', 'invalid', 'pending', 'touched', 'untouched', 'passed', 'failed', 'errors']" :key="attr" class="">
                    <div class="d-inline mr-3">{{ attr }}</div>
                    <div class="d-inline">{{ slotProps[attr] }}</div>
                </div>
            </div>

        </form>

    </validation-observer>
</template>

<script>
//import CustomFormCompInput from "./CustomFormCompInput.vue";
import CustomFormSchema from './CustomFormSchema';
import cloneDeep from 'lodash/cloneDeep';
import { schema } from './custom_form_comp_data';


/* DESIRED API:
- pass in schema as prop
- pass in initial data as prop
- method to check if valid, on demand
- emit event about validation status... maybe with other things?  like data, isDirty, etc.
- pass in extra context data?
- form-level read-only mode, display mode?
- schema must handle rendering vue components?  globally registered ones
- handle multiple pages natively?  or render that in tabs?
- call submit from external?  only that?  should make it async method of course
- if you want one with built-in submit, put a wrapper around it
- get data (if valid?) / get data (even if not valid?)
*/


export default {
    name: 'CustomFormCompForm',
    components: {
        //CustomFormCompInput,
        CustomFormSchema,
    },
    props: {
        schema: {
            type: Array,
            //required: true,
            default: () => [...schema],
        },
        initialData: {  // just the INITIAL data?  not v-model?  then set currentData based on the children reporting up their update?
            type: Object,
            default: () => ({}),
        },
        disabled: {  // set true for read-only, makes all children read-only / disabled
            // also allow json-logic conditional object here?  if so, make sure to evaluate it and pass down resolved value 
            type: Boolean,
            default: false,
        },
        displayMode: {
            type: Boolean,
            default: false,
        },
        context: {  // any 'extra' stuff - pass to children via props or provide/inject function
            // maybe better to have children get it via provide/inject function, in case it changes here, you can explicitly tell all children to re-validate/re-update, instead of needing deep watchers on all inputs
            type: Object,
            default: () => ({}),
        },
    },
    data: function() {
        return {
            //originalData: {},  // stash any original data here?
            currentData: {},
            lastEmittedDataStringified: null,  // set this whenever you emit update
            //schema: schema,
            allValid: null,  // from bottom up... not from observer
            allPassed: null,  // from bottom up... not from observer... just a test
        };
    },
    provide: function() {
        return {
            foo: 'bar',
            /* getRootData: () => {  // MOVED TO THE ROOT SCHEMA INSTEAD
                return this.currentData;
            }, */
            getRootContext: () => {
                return this.context;
            },
        };
    },
    computed: {
        localDisabled() {
            return this.disabled || this.displayMode;
        },
    },
    methods: {
        // METHODS TO CALL FROM PARENT - PUBLIC METHODS  (used internally too)
        getData() {
            // get current data (cloneDeep version), no matter the validity
            // maybe have multiple 'flavors' of this - getDataForApi(), getDataNative() ??? to preserve objects like datetimes, etc.?
            return cloneDeep(this.currentData);
        },
        setData(data) {
            // set new data in the form - make sure to cascade all the way down into fields/groups
            // still allow this even if form is disabled???
            const schemaComp = this.$refs['rootFormSchema'];
            schemaComp?.setData(data);
            //this.currentData = data;  // cloneDeep here? 
            /// need to trigger normal update logic to emit stuff to parent... or let child schema do that when it updates?
        },
        resetValidation() {
            return this.$refs.formObserver?.reset();  // no return actually
        },
        async runValidation() {
            return this.$refs.formObserver.validate().then(allValid => {
                if (!allValid) {
                    console.log("NOT ALL VALID!!!");
                    return false;
                } else {
                    console.log('All valid... carry on, carry on.');
                    return true;
                }
            });
        },
        // INTERNAL / PRIVATE METHODS
        handleUpdate(updatePayload) {
            const { data, valid, passed } = updatePayload;
            this.allValid = valid;
            this.allPassed = passed;
            //console.log('from handleUpdate', updatePayload);
            const newDataStringified = JSON.stringify(data);
            console.log('IN FORM handleUpdate from schema: ', newDataStringified);
            // ONLY set new currentData if NEEDED, otherwise we trigger another re-render
            // or maybe we need that to update other rendered things???
            if (newDataStringified !== this.lastEmittedDataStringified) {
                this.currentData = cloneDeep(data);
                this.lastEmittedDataStringified = newDataStringified;
                this.$emit('update', this.currentData);
            } else {
                console.log(`not emitting @update from FORM - data did not change`);
            }
        },
        handleRootSchemaMounted(mountedPayload) {
            // payload is the schema component itself
            console.log('rootSchemaMounted in form', mountedPayload);
        },
        onSubmit() {  // call this from slotProps.handleSubmit() provided by validation-observer
            alert("Form submitted!");
        },
    },
};
</script>
